import { lazy, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useLocation } from 'react-router-dom';

import isDesktop from 'helpers/isDesktop';
import withSuspense from 'helpers/withSuspense';
import useMount from 'hooks/useMount';
import useFullScreen from 'hooks/useFullScreen';
import { autoSignIn, fetchAllTeams, signOut } from 'store/actions';
import Layout from 'layout';
import Help from 'pages/help';
import UserProfile from 'pages/userProfile';
import PrivateRoute from 'components/privateRoute';
import { useSelector } from 'react-redux';
import { getUser } from 'store/selectors';
import { jwtDecode } from 'jwt-decode';
import { isTokenExpired } from 'helpers/utils';

const Home = withSuspense(lazy(() => import('pages/home')));
const Auth = withSuspense(lazy(() => import('pages/auth')));
const Clubs = withSuspense(lazy(() => import('pages/clubs')));
const Teams = withSuspense(lazy(() => import('pages/teams')));
const Matches = withSuspense(lazy(() => import('pages/matches')));
const RePlayer = withSuspense(lazy(() => import('pages/rePlayer')));
const NotFound = withSuspense(lazy(() => import('pages/notFound')));
const NationalFederation = withSuspense(lazy(() => import('pages/nf')));
const LeaderBoard = withSuspense(lazy(() => import('pages/leaderBoard')));
const HandHistory = withSuspense(lazy(() => import('pages/handHistory')));
const InviteFriend = withSuspense(lazy(() => import('pages/inviteFriend')));
const TermsAndConditions = withSuspense(lazy(() => import('pages/termsAndConditions')));
const CreateMatch = withSuspense(lazy(() => import('pages/matches/create')));

const HomeSkeleton = lazy(() => import('skeletons/home'));
const UserProfileSkeleton = lazy(() => import('skeletons/userProfile'));

function Main() {
  useFullScreen();

  const dispatch = useDispatch();

  const user = useSelector(getUser);

  const { search, pathname } = useLocation();

  useMount(() => {
    const queryParams = new URLSearchParams(search);
    const autoLoginToken = queryParams.get('token');

    autoLoginToken && dispatch(autoSignIn(autoLoginToken));

    const token = localStorage.getItem('mpToken');

    token && dispatch(autoSignIn(token));
  });

  useEffect(() => {
    if (Object.keys(user).length) {
      const decodedJWT = jwtDecode(user.token);
      if (isTokenExpired(decodedJWT?.exp)) {
        dispatch(signOut());
      }
      dispatch(fetchAllTeams());
    }
  }, [user]);

  return (
    <Switch>
      <Route exact path="/auth/:action" component={Auth} />
      <Redirect exact from="/auth" to="/auth/sign-in" component={Auth} />
      <Route exact path="/terms-and-conditions" component={TermsAndConditions} />
      <Layout>
        <Switch>
          <PrivateRoute exact path="/" component={Home} skeleton={HomeSkeleton} />
          <Route path="/matches" component={Matches} />
          <PrivateRoute path="/clubs" component={Clubs} />
          <Route path="/teams/:teamId?" component={Teams} />
          <PrivateRoute exact path="/replayer" component={RePlayer} />
          <PrivateRoute exact path="/user-profile/help" component={Help} />
          <PrivateRoute exact path="/leaderboard" component={LeaderBoard} />
          <PrivateRoute exact path="/hand-history" component={HandHistory} />
          <PrivateRoute exact path="/match/create" component={CreateMatch} />
          <PrivateRoute exact path="/invite-friend" component={InviteFriend} />
          <PrivateRoute
            exact
            path="/user-profile"
            component={UserProfile}
            skeleton={UserProfileSkeleton}
          />
          {isDesktop && <Route path="/nf" component={NationalFederation} />}
          <Route path="*" component={NotFound} />
        </Switch>
      </Layout>
    </Switch>
  );
}

export default Main;
